import React, { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ACCEPT_ATTR, hasFiles, validateFiles } from './validateFiles';

interface UploadDropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  uploading?: boolean;
  className?: string;
}

export function UploadDropzone({ onFiles, disabled, uploading, className }: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = (files: File[]) => {
    if (!files.length) return;
    const { valid, rejected } = validateFiles(files);
    setError(
      rejected.length
        ? rejected.map((r) => `"${r.name}": ${r.reason}`).join('; ')
        : null,
    );
    if (valid.length) onFiles(valid);
  };

  const open = () => {
    if (!disabled) inputRef.current?.click();
  };

  const onDragEnter = (e: React.DragEvent) => {
    if (disabled || !hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    depth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (disabled || !hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!hasFiles(e.dataTransfer)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  };
  const onDrop = (e: React.DragEvent) => {
    if (!hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (disabled) return;
    handle(Array.from(e.dataTransfer.files));
  };

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-label="Upload documents: click or drop files here"
        aria-describedby="kb-dropzone-hint"
        data-testid="kb-dropzone"
        data-dragging={dragging || undefined}
        onClick={open}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          'flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors',
          'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background',
          error
            ? 'border-destructive/60 bg-destructive/5'
            : dragging
              ? 'border-primary bg-primary/10'
              : 'border-border hover:border-primary/60 hover:bg-muted/50',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <p className="font-medium">
          {uploading
            ? 'Uploading...'
            : dragging
              ? 'Drop files to upload'
              : 'Drag files here or click to choose'}
        </p>
        <p id="kb-dropzone-hint" className="text-sm text-muted-foreground">
          PDF, DOCX, TXT, MD · up to 20 MB each · several files at once
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          tabIndex={-1}
          data-testid="kb-file-input"
          accept={ACCEPT_ATTR}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            handle(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          Not uploaded: {error}
        </p>
      )}
    </div>
  );
}
